import React, { useState, useEffect, useCallback } from 'react';
import { Overlay, Modal, Header, Title, Subtitle, SaveButton, Content, Card, Question, Segmented, SegmentButton, HelperText, Footer, CloseIcon } from './style';
import infoIcon from '../../static/icons/info-icon.svg';
import closeicon from '../../static/icons/close-icon.svg';
import { getAdvancedMarkingTeacherSettings, updateAdvancedMarkingTeacherSettings } from '../../service';
import {
  SPECIFIC_COMMENT_OPTIONS,
  AI_REPLY_OPTIONS,
  SUMMARY_LENGTH_OPTIONS,
  FEEDBACK_SUPPORT_OPTIONS,
  RUBRIC_DIFFICULTY_OPTIONS,
} from '../../utils/advancedMarking';
import { toast } from 'react-toastify';

// This is a popup for setting marking preferences
const MarkingPreferencesPopup = ({ setShowMarkingPreferencesPopup, onSave }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [annotations, setAnnotations] = useState('BALANCED');
  const [guidance, setGuidance] = useState('SLIGHTLY_IMPROVED');
  const [summaryLength, setSummaryLength] = useState('FULL');
  const [feedbackSupport, setFeedbackSupport] = useState('NEXT_LEVEL_IMPROVEMENT');
  const [gradingStandard, setGradingStandard] = useState('BALANCED');

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const settings = await getAdvancedMarkingTeacherSettings();
      if (settings) {
        setAnnotations(settings.specificCommentQuantity || 'BALANCED');
        setGuidance(settings.aiReplyDetail || 'SLIGHTLY_IMPROVED');
        setSummaryLength(settings.summaryLength || 'FULL');
        setFeedbackSupport(settings.sophisticationEnabled ? 'EXEMPLARY_PERFORMANCE' : 'NEXT_LEVEL_IMPROVEMENT');
        setGradingStandard(settings.rubricDifficulty || 'BALANCED');
      }
    } catch (error) {
      // Surface the failure instead of silently rendering hardcoded defaults as
      // if they were the teacher's saved preferences (which they'd then "save"
      // back, overwriting their real settings).
      console.error('Error fetching marking preferences:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSavePreferences = async () => {
    if (saving) return; // guard against double-submit while the PATCH is in flight
    setSaving(true);
    try {
      const payload = {
        specificCommentQuantity: annotations,
        aiReplyDetail: guidance,
        summaryLength: summaryLength,
        sophisticationEnabled: feedbackSupport === 'EXEMPLARY_PERFORMANCE',
        rubricDifficulty: gradingStandard,
      };
      await updateAdvancedMarkingTeacherSettings(payload);
      setShowMarkingPreferencesPopup(false);
      if (onSave) {
        onSave();
        toast.success("Marking preferences updated successfully")
      }
    } catch (error) {
      // Previously the modal silently stayed open on failure with no signal.
      console.error('Error saving marking preferences:', error);
      toast.error("Couldn't save your preferences — please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  if (loadError) {
    return (
      <Overlay onClick={() => setShowMarkingPreferencesPopup(false)}>
        <Modal onClick={(e) => e.stopPropagation()}>
          <CloseIcon src={closeicon} alt="close" onClick={() => setShowMarkingPreferencesPopup(false)} />
          <Header>
            <div>
              <Title>Your marking preferences</Title>
              <Subtitle>We couldn't load your saved preferences. Please try again.</Subtitle>
            </div>
            <SaveButton onClick={fetchSettings}>Retry</SaveButton>
          </Header>
        </Modal>
      </Overlay>
    );
  }

  const currentAnnotation = SPECIFIC_COMMENT_OPTIONS.find(o => o.value === annotations) || SPECIFIC_COMMENT_OPTIONS[1];
  const currentGuidance = AI_REPLY_OPTIONS.find(o => o.value === guidance) || AI_REPLY_OPTIONS[2];
  const currentSupport = FEEDBACK_SUPPORT_OPTIONS.find(o => o.value === feedbackSupport) || FEEDBACK_SUPPORT_OPTIONS[0];
  const currentGrading = RUBRIC_DIFFICULTY_OPTIONS.find(o => o.value === gradingStandard) || RUBRIC_DIFFICULTY_OPTIONS[1];


  return (
    <Overlay onClick={() => setShowMarkingPreferencesPopup(false)}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <CloseIcon
          src={closeicon}
          alt="close"
          onClick={() => setShowMarkingPreferencesPopup(false)}
        />
        <Header>
          <div>
            <Title>Your marking preferences</Title>
            <Subtitle>
              Set your marking preferences to help JeddAI tailor feedback to your style
            </Subtitle>
          </div>
          <SaveButton onClick={handleSavePreferences} disabled={saving}>{saving ? 'Saving…' : 'Save Preferences'}</SaveButton>
        </Header>

        <Content>
          {/* Section 1: In-text Annotations Quantity */}
          <Card>
            <Question>
              How many in-text annotations would you like to provide students on their work?
            </Question>

            <Segmented>
              {SPECIFIC_COMMENT_OPTIONS.map(option => (
                <SegmentButton
                  key={option.value}
                  active={annotations === option.value}
                  onClick={() => setAnnotations(option.value)}
                >
                  {option.label}
                </SegmentButton>
              ))}
            </Segmented>

            <HelperText>
              <img src={infoIcon} alt="info-icon" />
              {currentAnnotation.helper}
            </HelperText>
          </Card>

          {/* Section 2: In-text Guidance Type */}
          <Card>
            <Question>
              How much guidance would you like your in-text annotations to provide?
            </Question>

            <Segmented>
              {AI_REPLY_OPTIONS.map(option => (
                <SegmentButton
                  key={option.value}
                  active={guidance === option.value}
                  onClick={() => setGuidance(option.value)}
                >
                  {option.label}
                </SegmentButton>
              ))}
            </Segmented>

            <HelperText>
              <img src={infoIcon} alt="info-icon" />
              {currentGuidance.helper}
            </HelperText>
          </Card>

          {/* Section 3: Summary Length */}
          <Card>
            <Question>
              How long would you like the summary comments to be?
            </Question>

            <Segmented>
              {SUMMARY_LENGTH_OPTIONS.map(option => (
                <SegmentButton
                  key={option.value}
                  active={summaryLength === option.value}
                  onClick={() => setSummaryLength(option.value)}
                >
                  {option.value === 'SHORT' ? 'Short' : 'Detailed'}
                </SegmentButton>
              ))}
            </Segmented>
          </Card>

          {/* Section 4: Feedback Support Goal */}
          <Card>
            <Question>
              When giving feedback, what do you primarily want it to support?
            </Question>

            <Segmented>
              {FEEDBACK_SUPPORT_OPTIONS.map(option => (
                <SegmentButton
                  key={option.value}
                  active={feedbackSupport === option.value}
                  onClick={() => setFeedbackSupport(option.value)}
                >
                  {option.label}
                </SegmentButton>
              ))}
            </Segmented>

            <HelperText>
              <img src={infoIcon} alt="info-icon" />
              {currentSupport.helper}
            </HelperText>
          </Card>

          {/* Section 5: Grading Standard */}
          <Card>
            <Question>
              What is your preferred standard when awarding grades?
            </Question>

            <Segmented>
              {RUBRIC_DIFFICULTY_OPTIONS.map(option => (
                <SegmentButton
                  key={option.value}
                  active={gradingStandard === option.value}
                  onClick={() => setGradingStandard(option.value)}
                >
                  {option.label}
                </SegmentButton>
              ))}
            </Segmented>

            <HelperText>
              <img src={infoIcon} alt="info-icon" />
              {currentGrading.helper}
            </HelperText>
          </Card>
          <Footer>
            <SaveButton onClick={handleSavePreferences} disabled={saving}>{saving ? 'Saving…' : 'Save Preferences'}</SaveButton>
          </Footer>

        </Content>

      </Modal>
    </Overlay>

  );
};

export default MarkingPreferencesPopup;
